/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable wc/guard-super-call */
import { createElement } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import CodeMirror, { EditorState, EditorView } from '@uiw/react-codemirror';
import * as allThemes from '@uiw/codemirror-themes-all';
import type { Extension } from '@codemirror/state';
import { minimap } from '@replit/codemirror-minimap';
import { jsonSchema as jsonSchemaBundle, jsonSchemaLinter, stateExtensions as jsonSchemaState } from 'codemirror-json-schema';
import type { JSONSchema7 } from 'json-schema';
import { lintGutter, linter } from '@codemirror/lint';
import { json } from '@codemirror/lang-json';

import { BaseElementWithGlobalStyles, customElement } from '../../base-element';
import { watch } from '../../base-element/directives/watch.directive';

import styles from './code-editor.scss?inline';

export type { Extension };

// themes' list: https://uiwjs.github.io/react-codemirror/#/theme/home
export type ThemeNames =
  | 'abcdef'
  | 'abyss'
  | 'androidstudio'
  | 'andromeda'
  | 'atomone'
  | 'aura'
  | 'basicDark'
  | 'basicLight'
  | 'bbedit'
  | 'bespin'
  | 'consoleDark'
  | 'consoleLight'
  | 'copilot'
  | 'darcula'
  | 'dracula'
  | 'duotoneDark'
  | 'duotoneLight'
  | 'eclipse'
  | 'githubDark'
  | 'githubLight'
  | 'gruvboxDark'
  | 'gruvboxLight'
  | 'kimbie'
  | 'material'
  | 'materialDark'
  | 'materialLight'
  | 'monokai'
  | 'monokaiDimmed'
  | 'noctisLilac'
  | 'nord'
  | 'okaidia'
  | 'quietlight'
  | 'red'
  | 'solarizedDark'
  | 'solarizedLight'
  | 'sublime'
  | 'tokyoNight'
  | 'tokyoNightDay'
  | 'tokyoNightStorm'
  | 'tomorrowNightBlue'
  | 'vscodeDark'
  | 'vscodeLight'
  | 'whiteDark'
  | 'whiteLight'
  | 'xcodeDark'
  | 'xcodeLight';

export const codeEditorThemeList = Object.entries(allThemes)
  .filter(([_key, value]) => typeof value !== 'function')
  .map(([key]) => key)
  .filter((key) => !key.includes('Style'))
  .filter((key) => !key.includes('Init'))
  .filter((key) => !key.includes('defaultSettings')) as ThemeNames[];

const getThemeExtension = (theme?: ThemeNames): Extension | undefined => {
  if (!theme) return undefined;
  const themeKey = theme as keyof typeof allThemes;
  const extension = allThemes[themeKey];
  if (!extension || typeof extension === 'function') {
    return undefined;
  }
  return extension as Extension;
};

@customElement('ps-code-editor')
export class CodeEditorWC extends BaseElementWithGlobalStyles {
  static styles = unsafeCSS(styles);

  root: Root;

  private editorTheme: Extension;

  @property({ type: String, reflect: true }) theme?: ThemeNames = 'basicLight';

  @watch('theme')
  handleThemeChange() {
    const extension = getThemeExtension(this.theme);
    if (extension) {
      this.editorTheme = extension;
      // Trigger a re-render when theme changes
      this.requestUpdate();
    }
  }

  @property() height?: string;

  @property({ type: Array }) extensions?: Extension[];

  @property() value?: string;

  @property({ type: Object }) onChange?: (value: string) => void;

  @property() name?: string;

  @property({ type: Boolean }) disabled?: boolean;

  @property({ type: Object }) jsonSchema?: JSONSchema7;

  @property({ type: Boolean }) enableJsonValidation?: boolean;

  private hiddenInput?: HTMLInputElement;

  // Helper to detect if content is JSON or if JSON extension is present
  private isJsonContent(): boolean {
    console.log('[CodeEditor] isJsonContent called');
    console.log('[CodeEditor] enableJsonValidation:', this.enableJsonValidation);
    console.log('[CodeEditor] value:', this.value?.substring(0, 100));

    // First check if JSON validation is explicitly enabled
    if (this.enableJsonValidation) {
      console.log('[CodeEditor] JSON validation explicitly enabled');
      return true;
    }

    // Check if JSON extension was explicitly provided
    if (this.extensions) {
      const hasJsonExt = this.extensions.some((ext) => {
        const extString = ext.toString();
        return extString.includes('json') || extString.includes('JSON');
      });
      if (hasJsonExt) {
        console.log('[CodeEditor] JSON extension found');
        return true;
      }
    }

    // If no extension provided, detect based on content
    if (!this.value || this.value.trim() === '') {
      console.log('[CodeEditor] No value to check');
      return false;
    }

    const trimmed = this.value.trim();
    // Check if content looks like JSON (starts with { or [)
    const looksLikeJson = trimmed.startsWith('{') || trimmed.startsWith('[');
    console.log('[CodeEditor] Content looks like JSON:', looksLikeJson);

    return looksLikeJson;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.root) {
      this.root = createRoot(this.renderRoot);
    }
    // Initialize theme on connect - always set it
    const extension = getThemeExtension(this.theme);
    if (extension) {
      this.editorTheme = extension;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Remove the hidden input
    if (this.hiddenInput) {
      this.hiddenInput.remove();
    }
  }

  private updateHiddenInput(value: string) {
    if (this.name && !this.hiddenInput) {
      this.hiddenInput = document.createElement('input');
      this.hiddenInput.type = 'hidden';
      this.hiddenInput.name = this.name;
      this.hiddenInput.value = this.value || '';
      this.appendChild(this.hiddenInput);
    }
    if (this.hiddenInput) {
      this.hiddenInput.value = value;
    }
  }

  public getValue() {
    return this.value;
  }

  render() {
    // Fallback: ensure theme is set if somehow not initialized
    if (!this.editorTheme) {
      const extension = getThemeExtension(this.theme);
      if (extension) {
        this.editorTheme = extension;
      }
    }

    // Build extensions array with JSON schema support if JSON is detected
    const hasJson = this.isJsonContent();

    // Filter out JSON lang extension if we're going to use jsonSchemaBundle
    // because jsonSchemaBundle includes JSON lang support
    const filteredExtensions = hasJson
      ? (this.extensions || []).filter((ext) => {
          const extString = ext.toString();
          return !(extString.includes('json') || extString.includes('JSON'));
        })
      : (this.extensions || []);

    const allExtensions = [...filteredExtensions, minimap()];

    // Automatically add JSON schema validation when JSON is detected
    if (hasJson) {
      console.log('[CodeEditor] JSON detected, adding validation extensions');
      console.log('[CodeEditor] jsonSchema:', this.jsonSchema);

      // Add individual JSON extensions to avoid autocomplete compatibility issues
      // Add JSON language support (includes basic syntax validation)
      allExtensions.push(json());

      // Add lint gutter to show error icons in the gutter
      allExtensions.push(lintGutter());

      // Add schema state if a schema is provided
      // Note: Not using jsonSchemaLinter due to version conflicts with @codemirror/view
      // The json() lang extension provides basic JSON syntax validation
      if (this.jsonSchema) {
        allExtensions.push(...jsonSchemaState(this.jsonSchema));
      }

      console.log('[CodeEditor] Added JSON validation extensions');
    }

    console.log('[CodeEditor] Total extensions:', allExtensions.length);

    this.root.render(
      createElement(CodeMirror, {
        height: this.height,
        extensions: allExtensions,
        value: this.value,
        onCreateEditor: (view: EditorView, state: EditorState) => {
          if (this.value) {
            this.updateHiddenInput(this.value); // Update hidden input value
          }
        },
        onChange: (value: string) => {
          this.value = value;
          this.updateHiddenInput(value); // Update hidden input value
          if (this.onChange) {
            this.onChange(value);
          }
        },
        theme: this.editorTheme,
        editable: !this.disabled,
        basicSetup: {
          highlightActiveLine: !this.disabled,
          highlightActiveLineGutter: !this.disabled,
          lintKeymap: true,
        },
      })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-code-editor': CodeEditorWC;
  }
  enum PSElementTagNameMap {
    'ps-code-editor' = 'ps-code-editor',
  }
}
